import type {
  TypeMetaDatas,
  TypeProduct,
  TypeProductStock,
  TypeVariations,
} from '@/types';

/**
 * Check if the product is simple
 * @param  {TypeProduct} product Product
 * @return {boolean}             True if is simple or false if not
 */
export const isSimple = (product: TypeProduct) =>
  product === 'simple' || product === 'SIMPLE' || product === 'SimpleProduct';

/**
 * Check if the product is variable
 * @param  {TypeProduct} product Product
 * @return {boolean}             True if is variable or false if not
 */
export const isVariable = (product: TypeProduct) =>
  product === 'variable' ||
  product === 'VARIABLE' ||
  product === 'VariableProduct';

/**
 * Check if the product is bundle
 * @param  {TypeProduct} product Product
 * @return {boolean}             True if is bundle or false if not
 */
export const isBundle = (product: TypeProduct) =>
  product === 'woosb' || product === 'WoosbProduct';

/**
 * Check if the product is external
 * @param  {TypeProduct} product Product
 * @return {boolean}             True if is external or false if not
 */
export const isExternal = (product: TypeProduct) =>
  product === 'external' ||
  product === 'EXTERNAL' ||
  product === 'ExternalProduct';

/**
 * Check if has stock
 * @param  {TypeProductStock} stock Stock
 * @return {boolean}                True if has stock or false if hasn't
 */
export const isInStock = (stock: TypeProductStock) =>
  stock === 'instock' || stock === 'IN_STOCK';

/**
 * Check if hasn't stock
 * @param  {TypeProductStock} stock Stock
 * @return {boolean}                True if hasn't stock or false if has
 */
export const isOutOfStock = (stock: TypeProductStock) =>
  stock === 'onbackorder' ||
  stock === 'ON_BACK_ORDER' ||
  stock === 'outofstock' ||
  stock === 'OUT_OF_STOCK';

/**
 * Check if is a mirakl product
 * @param {TypeMetaDatas} metaData Metadata of the product
 * @returns {boolean} True if it is a mirakl product
 */
export const isMirakl = (metaData: TypeMetaDatas): boolean =>
  metaData?.some(
    (data) => data.key === 'is_mirakl_product' && data.value === 'on'
  ) ?? false;

/**
 * Check if the product has disabled the stocks alers
 * @param {TypeMetaDatas} metaData Metadata of the product
 * @returns {boolean} True if the product has disabled the stocks alers
 */
export const hasDisableStockAlert = (metaData: TypeMetaDatas): boolean =>
  metaData?.some(
    (meta) => meta.key === 'disable_stock_alert' && meta.value === 'yes'
  ) ?? false;

/**
 * Get the seller name of the product if it is available
 * @param {TypeVariations} variations Metadata of the product
 * @returns {string | null} The seller name or null if it is not available
 */
export const getSellerName = (variations: TypeVariations): string | null =>
  variations?.find((variation) => variation.name === 'seller')?.value ?? null;
